import React, { useMemo } from 'react'

import dayjs from 'utils/dayjs'

import Box from 'components_new/atoms/Box'
import Table from 'components_new/atoms/Table'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableRow from 'components_new/atoms/Table/TableRow'
import Text from 'components_new/atoms/Text'

import GenericListItem from 'components_new/molecules/GenericListItem'

import {
  TDashboardActivityPeriod,
  TDashboardUserActivity,
  filterViewsByPeriod
} from '../utils'
import { TDashboardGroupActivity } from 'types/GlobalDashboardGroup'
import { parseElapsedTimeSince } from 'helpers/Functions/Date'
import { useTranslation } from 'language/translator'
import { formatUsername } from 'utils/functions'

interface UsersViewProps {
  data: TDashboardGroupActivity
  period: TDashboardActivityPeriod
}

const UsersView = (props: UsersViewProps) => {
  const { data, period } = props
  const { t } = useTranslation()

  const filteredData = useMemo(() => {
    const filteredViews = filterViewsByPeriod(
      data.views,
      data.created_at,
      period
    ).views

    const activityMap: {
      [userId: string]: { views: number; latestActivity: string | null }
    } = {}

    filteredViews.forEach((view) => {
      const { user_id, date } = view

      if (!activityMap[user_id]) {
        activityMap[user_id] = { views: 0, latestActivity: null }
      }

      activityMap[user_id].views += 1

      if (
        !activityMap[user_id].latestActivity ||
        dayjs(date).isAfter(activityMap[user_id].latestActivity)
      ) {
        activityMap[user_id].latestActivity = date
      }
    })

    const userActivities: TDashboardUserActivity[] = Object.keys(data.accounts)
      .map((userId) => {
        const user = data.accounts[userId]
        const activity = activityMap[userId] || {
          views: 0,
          latestActivity: null
        }

        return {
          user_id: userId,
          name: formatUsername(user),
          email: user.email,
          views: activity.views,
          latest_activity: activity.latestActivity
        }
      })
      .sort((a, b) => b.views - a.views)

    return userActivities
  }, [period])

  return (
    <Box
      sx={{
        p: 2,
        gap: 2,
        minWidth: 0,
        bgcolor: 'background.default',
        borderRadius: 2
      }}
    >
      <TableContainer>
        <Table size="small" stickyHeader={true}>
          <TableHead sx={{ position: 'sticky' }}>
            <TableRow>
              <TableCell>{t('global', 'users')}</TableCell>
              <TableCell align="right">{t('global', 'views')}</TableCell>
              <TableCell>{t('activity_overview', 'latest_activity')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((user) => {
              return (
                <TableRow key={user.email}>
                  <TableCell>
                    <GenericListItem
                      body={user.email}
                      component="div"
                      disablePadding={true}
                      size="medium"
                      sx={{ p: 0 }}
                      title={user.name}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Text fontWeight="medium" variant="body2">
                      {user.views}
                    </Text>
                  </TableCell>
                  <TableCell>
                    {user.latest_activity
                      ? parseElapsedTimeSince(user.latest_activity, t)
                      : t('activity_overview', 'no_activity')}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default UsersView
